import React, { useState, useRef } from "react";
import { Modal, Button, ProgressBar } from "react-bootstrap";
import { uploadStopsExcelApi, uploadTripExcelApi } from "./stops-api";
import "../common/Util.css";

const StopsExcelUploadModal = ({ show, type, onHide, onUploadSuccess }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadMessage, setUploadMessage] = useState("");
  const [uploadStatus, setUploadStatus] = useState(null);
  const fileInputRef = useRef(null);

  const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = [
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];
      if (validTypes.includes(file.type)) {
        setSelectedFile(file);
        setUploadMessage("");
        setUploadStatus(null);
      } else {
        setUploadMessage("Please select a valid Excel file (.xls or .xlsx)");
        setUploadStatus("error");
        setSelectedFile(null);
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect({ target: { files: [file] } });
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadMessage("Please select a file first");
      setUploadStatus("error");
      return;
    }

    setUploading(true);
    setUploadProgress(5);

    try {
      // convert file to base64
      const toBase64 = (file) =>
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(file);
          reader.onload = () => resolve(reader.result);
          reader.onerror = (error) => reject(error);
        });

      const dataUrl = await toBase64(selectedFile);
      // strip data:*/*;base64, prefix
      const base64 = dataUrl.split(",")[1] || dataUrl;

      setUploadProgress(30);

      const payload = {
        type: "without-order",
        file: `data:@file/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${base64}`,
      };
      let response;
      if (type === "Stop") {
        response = await uploadStopsExcelApi(payload);
      } else {
        response = await uploadTripExcelApi(payload);
      }

      if (response?.data?.success === true) {
        setUploadMessage(response.data.message || "File uploaded successfully");
        setUploadStatus("success");
        setUploadProgress(100);
        if (response?.data?.base64) {
          const base64Str = response.data.base64;
          const filename =
            type === "Stop"
              ? "stops_upload_success_report.xlsx"
              : "trips_upload_success_report.xlsx";
          const contentType =
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

          try {
            // Strip data URI prefix if present
            const cleanBase64 =
              base64Str.indexOf("data:") === 0
                ? base64Str.split(",")[1] || base64Str
                : base64Str;

            const base64ToBlob = (b64, mime) => {
              const byteChars = atob(b64);
              const byteNumbers = new Array(byteChars.length);
              for (let i = 0; i < byteChars.length; i++) {
                byteNumbers[i] = byteChars.charCodeAt(i);
              }
              const byteArray = new Uint8Array(byteNumbers);
              return new Blob([byteArray], { type: mime });
            };

            const blob = base64ToBlob(cleanBase64, contentType);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
          } catch (downloadErr) {
            console.error("Error downloading error report:", downloadErr);
          }
        }

        // Reset form after 1.5 seconds
        setTimeout(() => {
          handleClose();
          onUploadSuccess?.();
        }, 1500);

        // Note: Backend may include url/base64 report even on success
        // We intentionally don't auto-download on success
      } else {
        // Upload failed - show error and download error report if available
        setUploadMessage(response?.data?.message || "Failed to upload file");
        setUploadStatus("error");

        // Only download error report file when upload fails
        if (response?.data?.base64) {
          const base64Str = response.data.base64;
          const filename =
            type === "Stop"
              ? "stops_upload_error_report.xlsx"
              : "trips_upload_error_report.xlsx";
          const contentType =
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

          try {
            // Strip data URI prefix if present
            const cleanBase64 =
              base64Str.indexOf("data:") === 0
                ? base64Str.split(",")[1] || base64Str
                : base64Str;

            const base64ToBlob = (b64, mime) => {
              const byteChars = atob(b64);
              const byteNumbers = new Array(byteChars.length);
              for (let i = 0; i < byteChars.length; i++) {
                byteNumbers[i] = byteChars.charCodeAt(i);
              }
              const byteArray = new Uint8Array(byteNumbers);
              return new Blob([byteArray], { type: mime });
            };

            const blob = base64ToBlob(cleanBase64, contentType);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
          } catch (downloadErr) {
            console.error("Error downloading error report:", downloadErr);
          }
        }
      }
    } catch (err) {
      console.error("Upload error:", err);
      setUploadMessage("Error uploading file. Please try again.");
      setUploadStatus("error");
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setUploadProgress(0);
    setUploadMessage("");
    setUploadStatus(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title className="fs-16 fw-600 textcolor">
          Import {type} from Excel
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="p-4">
        <div className="mb-3">
          <label className="fs-13 fw-500 textcolor mb-2 d-block">
            Upload Excel File (.xlsx, .xls)
          </label>

          <div
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            style={{
              border: "2px dashed #3469E7",
              borderRadius: "8px",
              padding: "32px",
              textAlign: "center",
              backgroundColor: "#F0F5FF",
              cursor: "pointer",
              transition: "all 0.3s ease",
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileSelect}
              style={{ display: "none" }}
            />

            <div className="fs-14 fw-500 textcolor mb-1">
              Drag & drop your file here
            </div>
            <div className="fs-12 fw-400 textGrey">or click to browse</div>
          </div>
        </div>

        {selectedFile && (
          <div
            className="p-2 mb-3 d-flex align-items-center justify-content-between"
            style={{
              backgroundColor: "#E8F5E9",
              borderRadius: "6px",
              border: "1px solid #4CAF50",
            }}
          >
            <div className="fs-13 fw-500 textcolor">✓ {selectedFile.name}</div>
            <button
              type="button"
              className="btn-close"
              onClick={() => setSelectedFile(null)}
              aria-label="Close"
            />
          </div>
        )}

        {uploading && (
          <div className="mb-3">
            <div className="fs-12 fw-500 textGrey mb-2">Uploading...</div>
            <ProgressBar
              now={uploadProgress}
              label={`${uploadProgress}%`}
              className="mb-2"
            />
          </div>
        )}

        {uploadMessage && (
          <div
            className="p-2 mb-3"
            style={{
              backgroundColor:
                uploadStatus === "success" ? "#E8F5E9" : "#FFEBEE",
              borderRadius: "6px",
              border:
                uploadStatus === "success"
                  ? "1px solid #4CAF50"
                  : "1px solid #F44336",
            }}
          >
            <div
              className="fs-12 fw-500"
              style={{
                color: uploadStatus === "success" ? "#2E7D32" : "#C62828",
              }}
            >
              {uploadMessage}
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="p-3">
        <Button
          variant="outline-secondary"
          onClick={handleClose}
          disabled={uploading}
          className="fs-13 fw-500"
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleUpload}
          disabled={!selectedFile || uploading}
          className="fs-13 fw-500"
        >
          {uploading ? "Uploading..." : "Upload"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default StopsExcelUploadModal;
